import authStorage from "src/utils/authStorage";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import consumer from "@/config/cable";
import api from "@/utils/api";
import { AuthContext } from "./AuthProvider";

const ConnectionContext = createContext();

export const useConnections = () => {
  const context = useContext(ConnectionContext);
  if (!context) {
    throw new Error("useConnections must be used within a ConnectionProvider");
  }
  return context;
};

export const ConnectionProvider = ({ children }) => {
  const { user } = useContext(AuthContext);

  // Initialize connections from user context (same approach as mobile)
  const getConnectionsFromUser = useCallback((userData) => {
    if (!userData) return { whatsapp: false, instagram: false, calendar: false, web: true };

    return {
      whatsapp:
        userData.whatsappConnected === true ||
        userData.wppConnected === true ||
        userData.metaWppConnected === true ||
        false,
      instagram: userData.instagramConnected === true || false,
      calendar: userData.connectedWithGoogle === true || false,
      web: true,
    };
  }, []);

  const [connections, setConnections] = useState(() => getConnectionsFromUser(user));
  const [loading, setLoading] = useState(true);
  const [lastFetch, setLastFetch] = useState(null);
  const hasFetchedFromApi = useRef(false);

  // Fetch all connection statuses from API (with user context as fallback)
  const fetchConnections = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);

      // Get user context values as fallback
      const userConnections = getConnectionsFromUser(user);

      // Fetch WhatsApp status from bot/status endpoint
      let whatsappConnected = userConnections.whatsapp;
      try {
        const botStatusResponse = await api.get("/partners/bot/status", {
          headers: { "Cache-Control": "no-cache" },
        });
        whatsappConnected = botStatusResponse?.data?.status_breakdown?.whatsapp_connected === true;
      } catch (err) {
        console.warn("Error fetching bot status, using user context:", err);
      }

      // Fetch other settings from partner_settings
      let instagramConnected = userConnections.instagram;
      let calendarConnected = userConnections.calendar;
      try {
        const settingsResponse = await api.get("/partners/partner_settings");
        const settings = settingsResponse?.data?.data?.attributes || {};
        instagramConnected = settings.instagram_connected === true || userConnections.instagram;
        calendarConnected = settings.google_calendar_connected === true || userConnections.calendar;
      } catch (err) {
        console.warn("Error fetching partner settings, using user context:", err);
      }

      setConnections({
        whatsapp: whatsappConnected,
        instagram: instagramConnected,
        calendar: calendarConnected,
        web: true,
      });

      hasFetchedFromApi.current = true;
      setLastFetch(Date.now());
    } catch (error) {
      console.warn("Error fetching connections:", error);
      // Fallback to user context on error
      setConnections(getConnectionsFromUser(user));
    } finally {
      setLoading(false);
    }
  }, [user, getConnectionsFromUser]);

  // Update a specific connection status
  const updateConnection = useCallback((type, status) => {
    setConnections((prev) => ({
      ...prev,
      [type]: status,
    }));
  }, []);

  // Update multiple connections at once
  const updateConnections = useCallback((updates) => {
    setConnections((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  // Refresh connections from API
  const refreshConnections = useCallback(async () => {
    await fetchConnections();
  }, [fetchConnections]);

  // Initial fetch when user is available
  useEffect(() => {
    if (user?.id) {
      fetchConnections();
    }
  }, [user?.id, fetchConnections]);

  // Sync from user context only on initial mount before API responds.
  // Once API has been fetched, the API is the source of truth — never overwrite
  // with stale user context data (fixes Issue F2: stale localStorage fallback).
  useEffect(() => {
    if (user && !hasFetchedFromApi.current) {
      const userConnections = getConnectionsFromUser(user);
      setConnections((prev) => ({
        ...prev,
        ...userConnections,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // WebSocket listener for real-time WhatsApp connection status
  useEffect(() => {
    if (!user?.id || !consumer?.subscriptions) return;

    const subscription = consumer.subscriptions.create(
      {
        channel: "WhatsappConnectionChannel",
        partner_id: String(user.id),
        token: authStorage.getItem("@sacgpt:token"),
      },
      {
        received: (data) => {
          const { event, payload } = data;
          if (event === "whatsapp_connected") {
            // Optimistic update for instant UI feedback
            setConnections((prev) => ({ ...prev, whatsapp: true }));
            // Refresh from API for full fresh state (per-assistant data, etc.)
            fetchConnections();
          } else if (event === "whatsapp_disconnected") {
            setConnections((prev) => ({ ...prev, whatsapp: false }));
            fetchConnections();
          } else if (event === "connection_status_changed" || event === "current_status") {
            if (payload?.connected !== undefined) {
              setConnections((prev) => ({ ...prev, whatsapp: payload.connected === true }));
            }
            fetchConnections();
          }
        },
      },
    );

    return () => {
      if (subscription) {
        try {
          consumer.subscriptions.remove(subscription);
        } catch (e) {
          /* cleanup */
        }
      }
    };
  }, [user?.id, fetchConnections]);

  const value = {
    connections,
    loading,
    lastFetch,
    updateConnection,
    updateConnections,
    refreshConnections,
  };

  return <ConnectionContext.Provider value={value}>{children}</ConnectionContext.Provider>;
};

export default ConnectionProvider;
